import { FilePdf, Spinner } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useMaterials, useMedia } from "@/hooks";
import type { Material } from "@/types";
import { Button, ControlledDropzone, errorToast, Input, Select } from "@/ui";

interface EditMaterialFormProps {
  material: Material;
  onClose: () => void;
}

interface FormValues {
  name: string;
  published: boolean;
  pdf?: File;
}

export const EditMaterialForm = ({
  material,
  onClose,
}: EditMaterialFormProps) => {
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const { useUpdateMaterial } = useMaterials();
  const { mutateAsync: updateMaterial, isPending } = useUpdateMaterial();

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: material.name,
      published: material.published,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const materialData: Partial<Material> = {
        name: data.name,
        published: data.published,
      };

      await updateMaterial({
        materialId: material.id,
        material: materialData,
      });

      // Upload new PDF if provided and wait for completion
      if (data.pdf) {
        const result = await upload(
          data.pdf,
          `/materials/${material.id}/pdf/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload PDF. Please try again.");
          return; // Keep modal open on upload failure
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["bundles"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["materials"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full flex-col gap-y-8 sm:w-[540px]"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Material Name"
          placeholder="e.g., MBCT-L Participant Handbook"
          {...register("name", { required: "Material name is required" })}
          error={errors.name?.message}
          required
        />

        <Select
          id="published"
          label="Published Status"
          options={[
            { value: "true", label: "Published" },
            { value: "false", label: "Unpublished" },
          ]}
          {...register("published", { required: true })}
          error={errors.published?.message}
        />

        {material.type === "pdf" && material.url && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-neutral-700">
              Current PDF
            </span>
            <a
              href={material.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md border border-neutral-300 p-3 text-sm text-primary-600 hover:bg-neutral-50"
            >
              <FilePdf size={20} />
              <span>View Current PDF</span>
            </a>
          </div>
        )}

        {material.type === "pdf" && (
          <ControlledDropzone
            name="pdf"
            control={control}
            label="Replace PDF (Optional)"
            placeholder="Upload new PDF file"
            accept={{ "application/pdf": [".pdf"] }}
          />
        )}
      </div>

      <div className="flex justify-center gap-2">
        <Button onClick={onClose} variant="outlined" className="flex-grow">
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-grow"
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Update Material"
          )}
        </Button>
      </div>
    </form>
  );
};
